import { ApiRoutes } from "@/lib/routeResolver";
import { cache } from "react";
import api from "./api";

// ─── Type Definitions ────────────────────────────────────────────────

export interface Testimonial {
  id: string;
  signature: string;
  titleMoreDescription: string;
  description: string;
  photo: string;
  createdDate: string;
  isActive: boolean;
}

interface TestimonialApiResponse {
  pageNumber: number;
  pageSize: number;
  count: number;
  totalPages: number;
  succeeded: boolean;
  data: Testimonial[];
}

// ─── Helpers ─────────────────────────────────────────────────────────

export { stripHtml, truncate } from "./helpers";

export function formatTestimonialDate(dateStr: string): string {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

// ─── Server-side Data Fetching ───────────────────────────────────────

export const fetchTestimonials = cache(
  async (
    page = 1,
    pageSize = 6,
  ): Promise<{
    items: Testimonial[];
    totalPages: number;
    count: number;
  }> => {
    const res = await api.get<TestimonialApiResponse>(ApiRoutes.WEBSITE_TESTIMONIAL, {
      params: {
        PageNumber: page,
        PageSize: pageSize,
        Status: "accepted",
        IsActive: true,
      },
    });

    const items = (res.data?.data ?? []).filter((t) => t.isActive);
    return {
      items,
      totalPages: res.data?.totalPages ?? 1,
      count: res.data?.count ?? 0,
    };
  },
);

// ─── Client-safe fetch (uses NEXT_PUBLIC_ env vars) ─────────────────

const BASE_URL = (process.env.NEXT_PUBLIC_API_URL || "").replace(/\/+$/, "");
const TOKEN = process.env.NEXT_PUBLIC_ACCESS_TOKEN!;
const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;

export async function fetchTestimonialsClient(
  page = 1,
  pageSize = 6,
): Promise<{
  items: Testimonial[];
  totalPages: number;
  count: number;
}> {
  const params = new URLSearchParams({
    PageNumber: String(page),
    PageSize: String(pageSize),
    CompanyId: COMPANY_ID,
    Status: "accepted",
    IsActive: "true",
  });

  const res = await fetch(`${BASE_URL}/website/testimonial?${params}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${TOKEN}`,
    },
  });
  const json = await res.json();
  const items: Testimonial[] = (json.data ?? []).filter(
    (t: Testimonial) => t.isActive,
  );
  return {
    items,
    totalPages: json.totalPages ?? 1,
    count: json.count ?? 0,
  };
}
